/**
 * Parts the tools build their shapes from. A shape (drawingTools.js) is
 * { strokes, fills, labels }, all in CSS px of the pane:
 * - stroke: { segments: [x0, y0, x1, y1][], color, width, opacity?, isDashed? };
 * - fill: { traps: [x0, top0, x1, top1, bottom0, bottom1][], color }, trapezoids with upright sides;
 * - label group: { color, align, axis, entries: [{ x, y, text } | { x, y, isPrice: true }] },
 *   axis: along which of them the entries follow each other ("x" or "y").
 */

/**
 * @param {{ color: string, width: number }} style
 * @param {number[][]} segments
 * @returns {object} a stroke in the colour and width of the drawing
 */
export const mainStroke = ({ color, width }, segments) => ({ segments, color, width });

/**
 * @param {{ isBackgroundVisible: boolean, backgroundColor: string }} style
 * @param {number[][]} traps
 * @returns {object[]} the fill of Background; none while it is switched off
 */
export const backgroundFills = ({ isBackgroundVisible, backgroundColor }, traps) =>
  isBackgroundVisible ? [{ traps, color: backgroundColor }] : [];

const yRangeAt = (x, vertices) => {
  const ys = [];

  vertices.forEach((from, index) => {
    const to = vertices[(index + 1) % vertices.length];

    if (x < Math.min(from.x, to.x) || x > Math.max(from.x, to.x)) return;

    if (from.x === to.x) ys.push(from.y, to.y);
    else ys.push(from.y + ((x - from.x) * (to.y - from.y)) / (to.x - from.x));
  });

  return [Math.min(...ys), Math.max(...ys)];
};

/**
 * A convex polygon as trapezoids with upright sides, from left to right.
 *
 * @param {Array<{ x: number, y: number }>} vertices - in the order of its outline
 * @returns {number[][]} traps of a fill
 */
export const polygonTraps = (vertices) => {
  const xs = [...new Set(vertices.map(({ x }) => x))].sort((first, second) => first - second);

  return xs.slice(1).map((right, index) => {
    const left = xs[index];
    const [leftTop, leftBottom] = yRangeAt(left, vertices);
    const [rightTop, rightBottom] = yRangeAt(right, vertices);

    return [left, leftTop, right, rightTop, leftBottom, rightBottom];
  });
};
